import { useRef } from 'react';
import { Helmet } from 'react-helmet';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import * as R from 'Router/routes';
import {
  Button,
  Card,
  Checkbox,
  Input,
  Link,
  Placeholder,
  Typography,
} from 'components';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import { PERSONAL_ID_MASK, PHONE_MASK } from 'utils';
import H1 from 'components/H/H1';
import styles from './PersonalDetails.module.scss';
import AccountAlreadyExistsBox from './AccountAlreadyExistsBox';
import usePersonalDetails from './usePersonalDetails';

export default function PersonalDetails() {
  const acceptAgreementRef = useRef<HTMLSpanElement>(null);
  const acceptProfilingRef = useRef<HTMLSpanElement>(null);
  const acceptNewsRef = useRef<HTMLSpanElement>(null);
  const acceptFaceIdProcessingRef = useRef<HTMLSpanElement>(null);

  const {
    formik,
    pending,
    isAccountAlreadyExistsError,
    handleLoginClick,
    availableConsents,
    availiableConsentsLoading,
    handleCheckboxChangeWithTouched,
    showErrorFormikField,
  } = usePersonalDetails();
  const { t } = useTranslation(['personal_details', 'common']);

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <div className={classes(styles['box-wrapper'], styles['title-wrapper'])}>
        <H1 dataTest="personalDetails-title">{t('title')}</H1>

        {isAccountAlreadyExistsError && (
          <div className={styles['warning-box']}>
            <AccountAlreadyExistsBox
              onClick={() => handleLoginClick(formik.values.email)}
            />
          </div>
        )}

        <Typography
          className={styles['form-info']}
          dataTest="personalDetails-info"
        >
          {t('info')}
        </Typography>
      </div>
      <form
        onSubmit={formik.handleSubmit}
        className={
          isAccountAlreadyExistsError
            ? styles['account-already-exists-form']
            : ''
        }
      >
        <div className={styles['box-wrapper']}>
          <div className={styles['field-container']}>
            <div className={styles['input-group']}>
              <Input
                classNameField={
                  !showErrorFormikField('firstName') && styles['input-first']
                }
                dataTest="first-name"
                name="firstName"
                autoComplete="given-name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTouched={formik.touched.firstName}
                placeholder={t('first_name')}
                error={formik.errors.firstName}
                stackable
                disabled={pending}
              />
              <Input
                classNameField={
                  !showErrorFormikField('lastName') && styles['input-middle']
                }
                dataTest="last-name"
                name="lastName"
                autoComplete="family-name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTouched={formik.touched.lastName}
                placeholder={t('last_name')}
                error={formik.errors.lastName}
                stackable
                disabled={pending}
              />
              <Input
                classNameField={
                  !showErrorFormikField('personalId') && styles['input-middle']
                }
                dataTest="personal-id"
                name="personalId"
                value={formik.values.personalId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTouched={formik.touched.personalId}
                placeholder={t('personal_id')}
                error={formik.errors.personalId}
                stackable
                mask={PERSONAL_ID_MASK}
                inputMode="numeric"
                disabled={pending}
              />
              <Input
                classNameField={
                  !showErrorFormikField('mobilePhone') && styles['input-last']
                }
                dataTest="mobile-phone"
                name="mobilePhone"
                autoComplete="tel"
                inputMode="tel"
                value={formik.values.mobilePhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTouched={formik.touched.mobilePhone}
                placeholder={t('mobile_phone')}
                error={formik.errors.mobilePhone}
                mask={PHONE_MASK}
                disabled={pending}
              />
            </div>
            <div className={styles['input-group']} />

            <div className={styles['input-group']}>
              <Typography
                className={styles.hint}
                size="m"
                fontWeight={700}
                kind="h3"
                dataTest="personalDetails-email-title"
              >
                {t('email_title')}
              </Typography>
              <Input
                dataTest="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTouched={formik.touched.email}
                placeholder={t('email')}
                error={formik.errors.email}
                disabled={pending}
              />
              <Typography
                className={styles.hint}
                size="xs"
                dataTest="personalDetails-email-hint"
              >
                {t('email_hint')}
              </Typography>
            </div>

            <div className={styles['input-group']}>
              <Input
                dataTest="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isTouched={formik.touched.password}
                placeholder={t('password')}
                error={formik.errors.password}
                showPasswordReveal
                disabled={pending}
                eventCollector={{ dontTrackContent: true }}
              />
              <Typography
                className={styles.hint}
                size="xs"
                dataTest="personalDetails-password-hint"
              >
                {t('password_hint')}
              </Typography>
            </div>
          </div>
        </div>

        <div className={styles['box-wrapper']}>
          <Typography
            className={styles.subtitle}
            size="m"
            kind="h3"
            fontWeight={700}
            dataTest="personalDetails-termsConditions-title"
          >
            {t('terms_and_conditions_title')}
          </Typography>
          <Typography
            className={styles.subtitle}
            size="s"
            dataTest="personalDetails-termsConditions-info-1"
          >
            {t('terms_and_conditions_info_1')}
          </Typography>
          <Typography
            className={styles.subtitle}
            size="s"
            dataTest="personalDetails-termsConditions-info-2"
          >
            {t('terms_and_conditions_info_2')}
          </Typography>
          <Typography
            className={styles.subtitle}
            size="m"
            kind="h3"
            dataTest="personalDetails-consents-title"
          >
            {t('consents_title')}
          </Typography>
        </div>

        <Card className={styles.card}>
          <Checkbox
            dataTest="accept-agreement"
            className={styles.checkbox}
            name="acceptAgreement"
            value={formik.values.acceptAgreement}
            isTouched={formik.touched.acceptAgreement}
            onChange={handleCheckboxChangeWithTouched}
            error={formik.errors.acceptAgreement}
            disabled={pending}
          >
            <Placeholder loading={availiableConsentsLoading}>
              {availableConsents?.ACCEPT_CREDIT_AGREEMENT.content[0].text && (
                <span
                  className={styles['checkbox-content']}
                  ref={acceptAgreementRef}
                  data-test="accept-agreement"
                  dangerouslySetInnerHTML={{
                    __html:
                      availableConsents.ACCEPT_CREDIT_AGREEMENT.content[0].text,
                  }}
                />
              )}
            </Placeholder>
          </Checkbox>
          <Checkbox
            dataTest="accept-profiling"
            className={styles.checkbox}
            name="acceptProfiling"
            value={formik.values.acceptProfiling}
            isTouched={formik.touched.acceptProfiling}
            onChange={handleCheckboxChangeWithTouched}
            error={formik.errors.acceptProfiling}
            disabled={pending}
          >
            <Placeholder loading={availiableConsentsLoading}>
              {availableConsents?.UNDERSTANDING_AGREEMENT_CONFIRMED.content[0]
                .text && (
                <span
                  className={styles['checkbox-content']}
                  ref={acceptProfilingRef}
                  data-test="accept-profiling"
                  dangerouslySetInnerHTML={{
                    __html:
                      availableConsents.UNDERSTANDING_AGREEMENT_CONFIRMED
                        .content[0].text,
                  }}
                />
              )}
            </Placeholder>
          </Checkbox>
          <Checkbox
            dataTest="accept-news"
            className={styles.checkbox}
            name="acceptNews"
            value={formik.values.acceptNews}
            isTouched={formik.touched.acceptNews}
            onChange={handleCheckboxChangeWithTouched}
            error={formik.errors.acceptNews}
            disabled={pending}
          >
            <Placeholder loading={availiableConsentsLoading}>
              {availableConsents?.ACCEPT_NEWS.content[0].text && (
                <span
                  className={styles['checkbox-content']}
                  ref={acceptNewsRef}
                  data-test="accept-news"
                  dangerouslySetInnerHTML={{
                    __html: availableConsents.ACCEPT_NEWS.content[0].text,
                  }}
                />
              )}
            </Placeholder>
          </Checkbox>
          <Checkbox
            dataTest="accept-face-id-processing"
            className={styles.checkbox}
            classNameContainer={styles['last-checkbox-container']}
            name="acceptFaceIdProcessing"
            value={formik.values.acceptFaceIdProcessing}
            isTouched={formik.touched.acceptFaceIdProcessing}
            onChange={handleCheckboxChangeWithTouched}
            error={formik.errors.acceptFaceIdProcessing}
            disabled={pending}
          >
            <Placeholder loading={availiableConsentsLoading}>
              {availableConsents?.ACCEPT_FACE_ID_PROCESSING.content[0].text && (
                <span
                  className={styles['checkbox-content']}
                  ref={acceptFaceIdProcessingRef}
                  data-test="accept-face-id-processing"
                  dangerouslySetInnerHTML={{
                    __html:
                      availableConsents.ACCEPT_FACE_ID_PROCESSING.content[0]
                        .text,
                  }}
                />
              )}
            </Placeholder>
          </Checkbox>
        </Card>

        <div className={styles['box-wrapper']}>
          <div>
            <Button
              pending={pending}
              className={styles.continue}
              type="submit"
              dataTest="submit_btn_basics"
            >
              {t('common:continue')}
            </Button>
          </div>
          <div className={styles.link}>
            <Link
              to={R.INTRO}
              disabled={pending}
              dataTest="personalDetails-link-intro"
            >
              <ChevronIcon
                className={styles.chevron}
                data-test="personalDetails-chevron-icon"
              />
              {t('common:back')}
            </Link>
          </div>
        </div>
      </form>
    </>
  );
}
